import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { createWebinar, updateWebinar } from '@/features/webinar/webinarThunks';
import type { Webinar, WebinarLanguage } from '@/features/webinar/types';
import type { AppError } from '@/features/auth/types';
import { mapHttpError } from '@/lib/mapApiError';
import { useSubmitLock } from '@/hooks/useSubmitLock';

const WEBINAR_TITLE_ALREADY_EXISTS = 'HTTP.ERROR.400.WEBINAR.TITLE.ALREADY_EXISTS';

function hasDuplicateTitleError(error: AppError | undefined): boolean {
  if (!error) return false;
  if (error.message === WEBINAR_TITLE_ALREADY_EXISTS) return true;
  if (!Array.isArray(error.details)) return false;
  return error.details.some(
    (d: { message?: string }) => d.message === WEBINAR_TITLE_ALREADY_EXISTS,
  );
}

const createGeneralFormSchema = (t: (key: string) => string) =>
  z.object({
    title: z
      .string()
      .min(1, t('generalForm.validation.titleRequired'))
      .refine((val) => val.trim().length > 0, {
        message: t('generalForm.validation.titleRequired'),
      })
      .max(100, t('generalForm.validation.titleMaxLength')),
    subtitle: z
      .string()
      .max(100, t('generalForm.validation.subtitleMaxLength'))
      .optional()
      .or(z.literal('')),
    description: z
      .string()
      .max(150, t('generalForm.validation.descriptionMaxLength'))
      .optional()
      .or(z.literal('')),
  });

type GeneralFormValues = {
  title: string;
  subtitle?: string;
  description?: string;
};

interface GeneralFormProps {
  webinar?: Webinar | null;
  onSuccess?: () => void;
  creationPrefill?: { title: string; subtitle: string; description: string } | null;
  /** Renvoyer `false` signale une création incomplète : le toast de succès n'est pas affiché. */
  onCreateComplete?: (createdWebinarId: number) => void | boolean | Promise<void | boolean>;
  creationFlowType?: 'LIVE' | 'EVERGREEN' | null;
  language?: WebinarLanguage;
}

export default function GeneralForm({
  webinar,
  onSuccess,
  creationPrefill,
  onCreateComplete,
  creationFlowType = null,
  language,
}: Readonly<GeneralFormProps>) {
  const { t } = useTranslation('createWebinar');
  const { t: tCommon } = useTranslation('common');
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const navigate = useNavigate();
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const isEditingExistingWebinar = Boolean(webinarIdParam);
  const [isEvergreenCreationPending, setIsEvergreenCreationPending] = useState(false);

  const hasDisplayedInitialContentRef = useRef(!isEditingExistingWebinar || Boolean(webinar));

  useEffect(() => {
    if (!hasDisplayedInitialContentRef.current && (!isEditingExistingWebinar || webinar)) {
      hasDisplayedInitialContentRef.current = true;
    }
  }, [isEditingExistingWebinar, webinar]);

  const generalFormSchema = createGeneralFormSchema(t);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
  } = useForm<GeneralFormValues>({
    resolver: zodResolver(generalFormSchema),
    defaultValues: {
      title: '',
      subtitle: '',
      description: '',
    },
    mode: 'onChange',
  });

  const { isLocked, runExclusive } = useSubmitLock();

  const isInitialMount = useRef(true);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!webinar && creationPrefill) {
      reset({
        title: creationPrefill.title ?? '',
        subtitle: creationPrefill.subtitle ?? '',
        description: creationPrefill.description ?? '',
      });
      hasInitialized.current = true;
      isInitialMount.current = false;
      return;
    }

    if (!webinar) {
      if (!hasInitialized.current) {
        hasInitialized.current = true;
        isInitialMount.current = false;
      }
      return;
    }

    reset({
      title: webinar.title ?? '',
      subtitle: webinar.shortDescription ?? '',
      description: webinar.description ?? '',
    });
    hasInitialized.current = true;
    isInitialMount.current = false;
  }, [webinar, creationPrefill, reset]);

  const submitGeneral = async (data: GeneralFormValues) => {
    if (!workspaceId) {
      console.error('Workspace ID is missing');
      return;
    }

    const webinarInput = {
      title: data.title.trim(),
      shortDescription: data.subtitle?.trim() || '',
      description: data.description?.trim() || '',
      ...(language != null && { language }),
    };

    if (webinar?.id) {
      const result = await dispatch(
        updateWebinar({
          workspaceId,
          id: webinar.id,
          input: webinarInput,
        }),
      );

      if (updateWebinar.fulfilled.match(result)) {
        toast.success(t('generalForm.saveSuccess.update'));
        onSuccess?.();
      } else if (updateWebinar.rejected.match(result)) {
        const error = result.payload as AppError | undefined;
        if (hasDuplicateTitleError(error)) {
          toast.error(t('generalForm.errors.duplicateTitle'));
        } else {
          const mainMessage = error?.message
            ? tCommon(error.message, error.message)
            : tCommon('errors.generic', 'Une erreur est survenue lors de la sauvegarde.');

          toast.error(mainMessage);

          if (error?.details && Array.isArray(error.details)) {
            error.details.forEach((detail: { message?: string }) => {
              if (detail.message === WEBINAR_TITLE_ALREADY_EXISTS) return;
              if (detail.message) {
                const mappedMessage = mapHttpError(detail.message);
                const translatedMessage = tCommon(mappedMessage, detail.message);
                toast.error(translatedMessage);
              }
            });
          }
        }
      }
    } else {
      const result = await dispatch(
        createWebinar({
          workspaceId,
          input: webinarInput,
        }),
      );

      if (createWebinar.fulfilled.match(result)) {
        const createdWebinarId = result.payload;
        const isEvergreenFlow = creationFlowType === 'EVERGREEN';

        if (onCreateComplete) {
          if (isEvergreenFlow) setIsEvergreenCreationPending(true);
          try {
            const completed = await onCreateComplete(createdWebinarId);
            if (completed !== false) {
              toast.success(
                t(
                  isEvergreenFlow
                    ? 'generalForm.evergreenCreated'
                    : 'generalForm.saveSuccess.create',
                ),
              );
            }
          } finally {
            if (isEvergreenFlow) setIsEvergreenCreationPending(false);
          }
        } else {
          toast.success(t('generalForm.saveSuccess.create'));
          navigate(`/create-webinar/configuration/${createdWebinarId}?openPresenters=true`, {
            replace: true,
          });
        }
      } else if (createWebinar.rejected.match(result)) {
        const error = result.payload as AppError | undefined;
        if (hasDuplicateTitleError(error)) {
          toast.error(t('generalForm.errors.duplicateTitle'));
        } else {
          const mainMessage = error?.message
            ? tCommon(error.message, error.message)
            : tCommon('errors.generic', 'Une erreur est survenue lors de la création.');

          toast.error(mainMessage);

          if (error?.details && Array.isArray(error.details)) {
            error.details.forEach((detail: { message?: string }) => {
              if (detail.message === WEBINAR_TITLE_ALREADY_EXISTS) return;
              if (detail.message) {
                const mappedMessage = mapHttpError(detail.message);
                const translatedMessage = tCommon(mappedMessage, detail.message);
                toast.error(translatedMessage);
              }
            });
          }
        }
      }
    }
  };

  const onSubmit = (data: GeneralFormValues) => runExclusive(() => submitGeneral(data));

  if (isEditingExistingWebinar && !hasDisplayedInitialContentRef.current) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-1">
          <Skeleton className="h-10 w-full" />
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-12" />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Skeleton className="h-10 w-full" />
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-12" />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Skeleton className="h-20 w-full" />
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-12" />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
    );
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="title"
        render={({ field }) => (
          <TextFieldWithLimit
            label={t('generalForm.title')}
            value={field.value ?? ''}
            onChange={(v) => field.onChange(v)}
            maxLength={100}
            error={errors.title?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="subtitle"
        render={({ field }) => (
          <TextFieldWithLimit
            label={t('generalForm.subtitle')}
            value={field.value ?? ''}
            onChange={(v) => field.onChange(v)}
            maxLength={100}
            error={errors.subtitle?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="description"
        render={({ field }) => (
          <TextFieldWithLimit
            label={t('generalForm.description')}
            value={field.value ?? ''}
            onChange={(v) => field.onChange(v)}
            maxLength={150}
            multiline
            rows={3}
            error={errors.description?.message}
          />
        )}
      />

      {isEvergreenCreationPending && (
        <div className="w-full">
          <p className=" text-secondary flex items-center gap-2">
            {t('generalForm.evergreenPending')}
          </p>
        </div>
      )}
      <div className="w-full">
        <Button
          type="submit"
          variant="default"
          disabled={isSubmitting || isLocked || !isValid}
          className="w-full"
        >
          {(isSubmitting || isLocked) && <Loader />}
          {t('generalForm.saveButton')}
        </Button>
      </div>
    </form>
  );
}
